import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { 
 User, 
 Menu, 
 X, 
 ChevronLeft,
 ChevronRight,
 Home,
 Car,
 Package,
 HelpCircle,
 LogOut,
 Crown,
 Calendar,
 DollarSign,
 Settings,
 Sparkles,
 Wrench,
 Clock,
 ShoppingBag,
 Globe,
 Lock,
 Key,
 Gem,
 RefreshCw,
 ExternalLink
} from 'lucide-react';
import { logoutUser, fetchUserProfile } from '../store/userSlice';
import { useSidebar } from '../contexts/SidebarContext';
import LoginModal from './LoginModal';

export default function Sidebar() {
 const { isCollapsed, toggleSidebar, isMobile, isMobileOpen, closeMobileSidebar } = useSidebar();
 // 移动端侧边栏始终展开显示，不使用折叠状态
 const collapsed = isMobile ? false : isCollapsed;
 const location = useLocation();
 const navigate = useNavigate();
 const dispatch = useDispatch();
 const { t, i18n } = useTranslation();
 const [showComingSoon, setShowComingSoon] = useState(false);
 const [comingSoonText, setComingSoonText] = useState('');
 const [forceUpdate, setForceUpdate] = useState(0);
 const [isRefreshing, setIsRefreshing] = useState(false);
 const [externalLinkModal, setExternalLinkModal] = useState({ show: false, url: '', name: '', description: '' });
 
 // 登录弹窗状态
 const [showLoginModal, setShowLoginModal] = useState(false);
 
 // 获取用户信息
 const { user, isLoggedIn } = useSelector((state) => state.user);
 
 // 处理登录需求
 const handleLoginRequired = () => {
 setShowLoginModal(true);
 };
 
 // 处理登录成功
 const handleLoginSuccess = () => {
 setShowLoginModal(false);
 };
 
 // 处理关闭登录弹窗
 const handleCloseModal = () => {
 setShowLoginModal(false);
 };

 // 处理刷新用户信息
 const handleRefreshUserInfo = async () => {
 if (!isLoggedIn || isRefreshing) return;
 
 setIsRefreshing(true);
 try {
 await dispatch(fetchUserProfile()).unwrap();
 // 显示成功提示（可选）
 console.log('用户信息刷新成功');
 } catch (error) {
 console.error('刷新用户信息失败:', error);
 } finally {
 setIsRefreshing(false);
 }
 };

 // 监听语言变化，强制重新渲染
 useEffect(() => {
 const handleLanguageChange = () => {
 setForceUpdate(prev => prev + 1);
 };

 // 监听i18n语言变化事件
 i18n.on('languageChanged', handleLanguageChange);
 
 // 监听自定义语言变化事件
 window.addEventListener('languageChanged', handleLanguageChange);

 return () => {
 i18n.off('languageChanged', handleLanguageChange);
 window.removeEventListener('languageChanged', handleLanguageChange);
 };
 }, [i18n]);

 // AI功能模块
 const aiFeatures = [
 // 暂时隐藏以下入口
 // {
 // name: t('sidebar.aiFeatures.aiChat'),
 // href: '/ai-chat/sidebar',
 // icon: MessageCircle,
 // requireAuth: true,
 // comingSoon: true
 // },
 // {
 // name: t('sidebar.aiFeatures.aiImage'),
 // href: '/ai-image/sidebar',
 // icon: Image,
 // requireAuth: true,
 // comingSoon: true
 // },
 // {
 // name: t('sidebar.aiFeatures.aiVideo'),
 // href: '/ai-video/sidebar',
 // icon: Video,
 // requireAuth: true,
 // comingSoon: true
 // },
 { 
 name: t('sidebar.aiFeatures.aiFleet'), 
 href: '/ai-fleet', 
 icon: Car,
 requireAuth: true 
 },
 {
 name: t('sidebar.aiFeatures.aiToolbox'),
 href: '/ai-toolbox/sidebar',
 icon: Wrench,
 requireAuth: true
 }
 ];

 // 用户服务模块
 const userServices = [
 {
 name: t('sidebar.userServices.settings'),
 href: '/settings',
 icon: Settings,
 requireAuth: true
 },
 { 
 name: t('sidebar.userServices.support'), 
 href: '/support-sidebar',
 icon: HelpCircle,
 requireAuth: false 
 }
 ];

 // AI服务模块
 const aiServices = [
 {
 name: t('sidebar.aiServices.officialProducts'),
 href: '#',
 icon: ShoppingBag,
 requireAuth: false,
 externalUrl: 'https://ai2you.org',
 description: '即将跳转到 GPT 官网成品号购买页面，您可以在那里购买已激活的 GPT Plus / ChatGPT 成品账号。'
 },
 {
 name: t('sidebar.aiServices.topUp'),
 href: '#',
 icon: ShoppingBag,
 requireAuth: false,
 externalUrl: 'https://ai2you.org',
 description: '即将跳转到代充值页面，您可以在那里为自己的 GPT / Claude 等 AI 产品账号进行充值或续订订阅。'
 }
 // 暂时隐藏 Claude Code / Codex 入口
 // {
 // name: t('sidebar.aiServices.claudeCodex'),
 // href: '#',
 // icon: Terminal,
 // requireAuth: false,
 // externalUrl: 'https://usezyla.com',
 // description: '即将跳转到 Claude Code / Codex 服务页面，您可以在那里购买或订阅 Claude Code、OpenAI Codex 等 AI 编程助手服务。'
 // }
 ];

 // 检查当前路径是否匹配
 const isActiveRoute = (href) => {
 if (href === '#') return false;
 
 if (href === '/orders-sidebar') {
 return location.pathname === '/orders-sidebar' || location.pathname === '/orders';
 }
 if (href === '/support-sidebar') {
 return location.pathname === '/support-sidebar' || location.pathname === '/support';
 }
 return location.pathname.startsWith(href);
 };

 // 处理登出
 const handleLogout = () => {
 dispatch(logoutUser());
 navigate('/');
 };

 // 处理外部链接点击
 const handleExternalLinkClick = (url, name, description) => {
 setExternalLinkModal({ show: true, url, name, description });
 };

 const handleExternalLinkConfirm = () => {
 window.open(externalLinkModal.url, '_blank', 'noopener,noreferrer');
 setExternalLinkModal({ show: false, url: '', name: '' });
 };

 const handleExternalLinkCancel = () => {
 setExternalLinkModal({ show: false, url: '', name: '' });
 };

 // 处理Coming Soon点击
 const handleComingSoonClick = (itemName) => {
 setComingSoonText(t('sidebar.status.comingSoonMessage', { feature: itemName }));
 setShowComingSoon(true);
 setTimeout(() => {
 setShowComingSoon(false);
 }, 3000);
 };

 // 渲染导航项
 const renderNavItem = (item) => {
 const Icon = item.icon;
 const isActive = isActiveRoute(item.href);
 
 if (item.comingSoon) {
 return (
 <li key={item.name}>
 <button
 onClick={() => handleComingSoonClick(item.name)}
 className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-300 group text-ink-400 hover:bg-cream-100 hover:text-ink-500 ${
 collapsed ? 'justify-center' : ''
 }`}
 title={collapsed ? `${item.name} (${t('sidebar.status.comingSoon')})` : ''}
 >
 <div className="relative">
 <Icon className="w-5 h-5 flex-shrink-0 text-ink-400 group-hover:text-ink-500" />
 {!collapsed && (
 <Clock className="w-3 h-3 text-accent-500 absolute -top-1 -right-1" />
 )}
 </div>
 {!collapsed && (
 <div className="flex items-center gap-2 flex-1">
 <span className="text-sm">{item.name}</span>
 <span className="text-xs bg-accent-100 text-accent-600 px-2 py-0.5 rounded-full font-medium">
 {t('sidebar.status.soon')}
 </span>
 </div>
 )}
 </button>
 </li>
 );
 }
 
 if (item.externalUrl) {
 return (
 <li key={item.name}>
 <button
 onClick={() => handleExternalLinkClick(item.externalUrl, item.name, item.description)}
 className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-300 group text-ink-700 hover:bg-cream-100 hover:text-ink-900 ${
 collapsed && !isMobile ? 'justify-center' : ''
 }`}
 title={collapsed && !isMobile ? item.name : ''}
 >
 <Icon className="w-5 h-5 flex-shrink-0 text-ink-500 group-hover:text-ink-700" />
 {!collapsed && <span className="text-sm truncate" title={item.name}>{item.name}</span>}
 </button>
 </li>
 );
 }

 return (
 <li key={item.name}>
 <Link
 to={item.href}
 onClick={handleMobileNavClick}
 className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-300 group ${
 isActive
 ? 'bg-accent-100 text-accent-600 font-semibold'
 : 'text-ink-700 hover:bg-cream-100 hover:text-ink-900'
 } ${collapsed && !isMobile ? 'justify-center' : ''}`}
 title={collapsed && !isMobile ? item.name : ''}
 >
 <Icon className={`w-5 h-5 flex-shrink-0 ${
 isActive ? 'text-accent-600' : 'text-ink-500 group-hover:text-ink-700'
 }`} />
 {!collapsed && (
 <span className="text-sm">{item.name}</span>
 )}
 </Link>
 </li>
 );
 };

 // 移动端点击导航项后自动关闭侧边栏
 const handleMobileNavClick = () => {
 if (isMobile) {
 closeMobileSidebar();
 }
 };

 return (
 <>
 {/* 外部链接跳转确认弹窗 */}
 {externalLinkModal.show && (
 <div className="fixed inset-0 bg-ink-900/40 backdrop-blur-sm z-50 flex items-center justify-center px-4">
 <div className="bg-cream-50 rounded-xl shadow-md border border-ink-200 w-full max-w-xs overflow-hidden">
 {/* 顶部图标区 */}
 <div className="bg-accent-500 px-6 pt-7 pb-5 flex flex-col items-center">
 <div className="w-12 h-12 bg-white/15 rounded-lg flex items-center justify-center mb-3 border border-white/20">
 <ExternalLink className="w-6 h-6 text-white" />
 </div>
 <h3 className="text-base font-semibold text-white">即将跳转</h3>
 </div>
 {/* 内容区 */}
 <div className="px-6 py-5">
 <p className="text-sm font-semibold text-ink-900 text-center mb-2">{externalLinkModal.name}</p>
 <p className="text-sm text-ink-600 text-center leading-relaxed">{externalLinkModal.description}</p>
 <p className="text-xs text-ink-400 text-center mt-3">确认后将在新标签页中打开</p>
 </div>
 {/* 按钮区 */}
 <div className="flex border-t border-ink-200">
 <button
 onClick={handleExternalLinkCancel}
 className="flex-1 py-3.5 text-sm text-ink-600 hover:bg-cream-100 transition-colors duration-200 font-medium"
 >
 取消
 </button>
 <div className="w-px bg-ink-200" />
 <button
 onClick={handleExternalLinkConfirm}
 className="flex-1 py-3.5 text-sm text-accent-600 hover:bg-accent-50 transition-colors duration-200 font-semibold"
 >
 前往
 </button>
 </div>
 </div>
 </div>
 )}

 {/* 移动端遮罩层 */}
 {isMobile && isMobileOpen && (
 <div
 className="fixed inset-0 bg-black/50 z-30 transition-opacity duration-300"
 onClick={closeMobileSidebar}
 />
 )}

 <aside className={`fixed left-0 top-0 h-full bg-cream-50 border-r border-ink-200 transition-all duration-300 z-40 flex flex-col ${
 isMobile
 ? `w-64 ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}`
 : collapsed ? 'w-20' : 'w-64'
 }`}>
 {/* 顶部Logo区域 */}
 <div className="bg-accent-500 flex-shrink-0">
 {!isMobile && collapsed ? (
 // 桌面端收缩状态：Logo和按钮分两行
 <>
 <div className="h-16 flex items-center justify-center">
 <Link
 to="/"
 className="flex items-center hover:opacity-80 transition-all duration-300"
 >
 <div className="w-10 h-10 bg-cream-50 rounded-lg flex items-center justify-center overflow-hidden border border-white/20">
 <img
 src="/images/logo192.png"
 alt="紫刀AI Logo"
 className="w-full h-full object-cover"
 onError={(e) => {
 e.target.style.display = 'none';
 e.target.nextSibling.style.display = 'block';
 }}
 />
 <span className="text-2xl hidden">🐵</span>
 </div>
 </Link>
 </div>
 <div className="pb-3 flex justify-center">
 <button
 onClick={toggleSidebar}
 className="p-2 rounded-md bg-white/10 hover:bg-white/20 transition-colors duration-200"
 >
 <ChevronRight className="w-4 h-4 text-white" />
 </button>
 </div>
 </>
 ) : (
 // 展开状态（桌面端展开 + 移动端始终展开）
 <div className="px-4 py-4">
 <div className="flex items-start justify-between">
 <Link
 to="/"
 onClick={handleMobileNavClick}
 className="flex items-start gap-3 hover:opacity-80 transition-all duration-300 min-w-0"
 >
 <div className="w-14 h-14 bg-cream-50 rounded-lg flex items-center justify-center overflow-hidden border border-white/20 flex-shrink-0 p-0.5">
 <img
 src="/images/logo192.png"
 alt="紫刀AI Logo"
 className="w-full h-full object-cover"
 onError={(e) => {
 e.target.style.display = 'none';
 e.target.nextSibling.style.display = 'block';
 }}
 />
 <span className="text-2xl hidden">🐵</span>
 </div>
 <div className="pt-0.5">
 <h1 className="text-base font-semibold text-white leading-tight">{t('sidebar.brandName')}</h1>
 <p className="text-[11px] text-white/70 mt-1 leading-relaxed">AI工具大全<br/>承包你的所有AI需求</p>
 </div>
 </Link>

 <button
 onClick={isMobile ? closeMobileSidebar : toggleSidebar}
 className="p-1.5 rounded-md bg-white/10 hover:bg-white/20 transition-colors duration-200 flex-shrink-0 mt-1"
 >
 {isMobile ? <X className="w-5 h-5 text-white" /> : <ChevronLeft className="w-5 h-5 text-white" />}
 </button>
 </div>
 </div>
 )}
 </div>

 {/* 导航菜单 - 占据剩余空间 */}
 <nav className="flex-1 overflow-y-auto py-4">
 {/* 导航菜单项 */}
 <div className="space-y-1">
 <ul className="space-y-1 px-3">
 {aiFeatures.map(renderNavItem)}
 </ul>

 {/* AI工具箱下方分割线 */}
 <div className="mx-3 my-4">
 <div className="border-t border-ink-200"></div>
 </div>

 <ul className="space-y-1 px-3">
 {aiServices.map(renderNavItem)}
 </ul>

 {/* AI服务和用户服务之间的分割线 */}
 <div className="mx-3 my-4">
 <div className="border-t border-ink-200"></div>
 </div>

 <ul className="space-y-1 px-3">
 {userServices.map(renderNavItem)}
 </ul>
 </div>

 {/* 语言切换器 - 在用户服务模块下方 */}
 <div className="mt-0">
 <ul className="space-y-1 px-3">
 </ul>
 </div>
 </nav>

 {/* 底部整合模块 */}
 {isLoggedIn && user && (
 <div className="flex-shrink-0">
 {/* 充值升级按钮 - 醒目的渐变设计 */}
 <div className={`${collapsed ? 'px-2' : 'px-4'} pb-3`}>
 <button
 onClick={() => navigate('/purchase')}
 className={`w-full relative overflow-hidden group ${
 collapsed ? 'p-3' : 'px-5 py-3'
 }`}
 >
 {/* 渐变背景 */}
 <div className="absolute inset-0 bg-gradient-to-r from-accent-500 via-accent-600 to-red-500 rounded-xl"></div>
 
 {/* 闪光效果 */}
 <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-xl"></div>
 
 {/* 内容 */}
 <div className="relative flex items-center justify-center gap-2">
 <Sparkles className={`${collapsed ? 'w-5 h-5' : 'w-4 h-4'} text-white`} />
 {!collapsed && (
 <>
 <span className="text-white font-semibold text-sm">{t('sidebar.actions.upgrade')}</span>
 <Crown className="w-4 h-4 text-accent-100" />
 </>
 )}
 </div>
 </button>
 </div>
 
 {/* 用户信息和设置 - 优雅的卡片设计 */}
 <div className={`border-t border-ink-200 bg-gradient-to-t from-cream-100 to-cream-50 ${
 collapsed ? 'p-2' : 'p-4'
 }`}>
 {/* 用户信息卡片 - 点击进入设置 */}
 <div
 role="button"
 tabIndex={0}
 onClick={() => navigate('/settings')}
 onKeyDown={(event) => {
 if (event.key === 'Enter' || event.key === ' ') {
 event.preventDefault();
 navigate('/settings');
 }
 }}
 className={`w-full bg-cream-100 rounded-lg border border-ink-200 hover:border-ink-900 transition-colors duration-200 ${
 collapsed ? 'p-2' : 'p-3'
 } hover:bg-cream-200/60 cursor-pointer focus:outline-none focus:ring-2 focus:ring-accent-300`}
 >
 {/* 用户信息 */}
 <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
 {/* 用户头像 */}
 <div className="relative">
 <div className="w-10 h-10 bg-accent-500 rounded-lg flex items-center justify-center">
 <User className="w-5 h-5 text-white" />
 </div>
 {/* 在线状态指示器 */}
 <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-cream-100 rounded-full"></div>
 {/* 收缩状态下的钻石数量指示器 */}
 {collapsed && user.diamond_count > 0 && (
 <div className="absolute -top-1 -left-1 w-5 h-5 bg-accent-500 rounded-full flex items-center justify-center">
 <Gem className="w-2.5 h-2.5 text-white" />
 </div>
 )}
 </div>
 
 {/* 用户信息文字 */}
 {!collapsed && (
 <div className="flex-1 min-w-0">
 <p className="text-sm font-semibold text-ink-900 truncate">
 {user.name || user.email || t('sidebar.user.user')}
 </p>
 <p className="text-xs text-ink-500 truncate">
 {(() => {
 const uid = user.uid || user.id || user.email || '';
 if (uid.length > 16) {
 return `${uid.substring(0, 6)}...${uid.substring(uid.length - 6)}`;
 }
 return uid;
 })()}
 </p>
 {/* 钻石数量显示 */}
 <div className="flex items-center gap-1 mt-1">
 <Gem className="w-3 h-3 text-accent-500" />
 <span className="text-xs text-accent-600 font-medium">
 {user.diamond_count || 0}
 </span>
 </div>
 </div>
 )}
 
 {/* 刷新按钮 - 展开状态显示在右侧 */}
 {!collapsed && (
 <button
 type="button"
 onClick={(e) => {
 e.stopPropagation(); // 阻止冒泡到外层按钮
 handleRefreshUserInfo();
 }}
 className={`p-2 rounded-lg transition-all duration-300 ${
 isRefreshing 
 ? 'text-accent-500 animate-spin' 
 : 'text-ink-400 hover:text-ink-600 hover:bg-cream-100'
 }`}
 title={isRefreshing ? '刷新中...' : '刷新用户信息'}
 disabled={isRefreshing}
 >
 <RefreshCw className="w-4 h-4" />
 </button>
 )}
 </div>
 
 {/* 收缩状态下的钻石数量 - 单独一行 */}
 {collapsed && (
 <div className="mt-2 flex justify-center">
 <div className="relative group">
 <div className="px-2 py-1 bg-accent-500 rounded-full flex items-center gap-1">
 <Gem className="w-3 h-3 text-white" />
 <span className="text-xs text-white font-medium">{user.diamond_count || 0}</span>
 </div>
 {/* 悬浮提示 */}
 <div className="absolute left-full ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-50">
 <div className="bg-ink-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
 钻石余额: {user.diamond_count || 0}
 </div>
 </div>
 </div>
 </div>
 )}
 
 {/* 收缩状态下的刷新按钮 - 单独一行 */}
 {collapsed && (
 <button
 type="button"
 onClick={(e) => {
 e.stopPropagation(); // 阻止冒泡到外层按钮
 handleRefreshUserInfo();
 }}
 className={`mt-2 w-full p-2 rounded-lg transition-all duration-300 flex justify-center ${
 isRefreshing 
 ? 'text-accent-500' 
 : 'text-ink-600 hover:bg-cream-100'
 }`}
 title={isRefreshing ? '刷新中...' : '刷新用户信息'}
 disabled={isRefreshing}
 >
 <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
 </button>
 )}
 </div>
 </div>
 </div>
 )}
 
 {/* 未登录状态的底部 */}
 {!isLoggedIn && (
 <div className="flex-shrink-0 bg-cream-100 border-t border-ink-200">
 <div className={`${collapsed ? 'p-2' : 'p-4'}`}>
 <button
 onClick={handleLoginRequired}
 className={`w-full bg-accent-500 text-white rounded-lg font-medium hover:bg-accent-600 transition-colors duration-200 ${
 collapsed ? 'p-2' : 'px-4 py-3'
 }`}
 >
 {collapsed ? (
 <User className="w-4 h-4 mx-auto" />
 ) : (
 <div className="flex items-center justify-center gap-2">
 <User className="w-4 h-4" />
 <span className="text-sm">{t('sidebar.actions.loginRegister')}</span>
 </div>
 )}
 </button>
 </div>
 </div>
 )}
 </aside>

 {/* Coming Soon 提示 */}
 {showComingSoon && (
 <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in-down">
 <div className="bg-cream-50 rounded-xl shadow-md border border-ink-200 px-5 py-3 flex items-center gap-3">
 <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center border border-accent-200">
 <Clock className="w-5 h-5 text-accent-600" />
 </div>
 <div>
 <p className="font-semibold text-ink-900">{comingSoonText}</p>
 <p className="text-sm text-ink-500">{t('sidebar.status.developingMessage')}</p>
 </div>
 </div>
 </div>
 )}

 {/* 登录弹窗 */}
 <LoginModal 
 isOpen={showLoginModal}
 onClose={handleCloseModal}
 onLogin={handleLoginSuccess}
 />
 </>
 );
} 
